const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
/** Offset of `timezone` from UTC at `at`, in minutes (positive east of Greenwich). */
export function tzOffsetMinutes(at, timezone) {
    const f = new Intl.DateTimeFormat("en-GB", {
        timeZone: timezone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
    const p = {};
    for (const part of f.formatToParts(at))
        p[part.type] = part.value;
    const asUtc = Date.UTC(Number(p["year"]), Number(p["month"]) - 1, Number(p["day"]), Number(p["hour"]), Number(p["minute"]), Number(p["second"]));
    return Math.round((asUtc - at.getTime()) / 60_000);
}
/** The UTC instant of `date` (YYYY-MM-DD) at `hhmm` wall-clock time in `timezone`. */
export function wallToUtc(date, hhmm, timezone) {
    const [y, m, d] = date.split("-").map(Number);
    const [h, min] = hhmm.split(":").map(Number);
    let guess = Date.UTC(y, m - 1, d, h, min);
    // Two passes handle the offset change across a DST boundary.
    for (let i = 0; i < 2; i++) {
        const off = tzOffsetMinutes(new Date(guess), timezone);
        guess = Date.UTC(y, m - 1, d, h, min) - off * 60_000;
    }
    return new Date(guess);
}
/** Calendar date (YYYY-MM-DD) and weekday of an instant in `timezone`. */
export function localDate(at, timezone) {
    const f = new Intl.DateTimeFormat("en-GB", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        weekday: "short",
    });
    const p = {};
    for (const part of f.formatToParts(at))
        p[part.type] = part.value;
    const wd = (p["weekday"] ?? "").slice(0, 3).toLowerCase();
    return {
        date: `${p["year"]}-${p["month"]}-${p["day"]}`,
        weekday: WEEKDAYS.includes(wd) ? wd : "mon",
    };
}
export function addDays(date, n) {
    const [y, m, d] = date.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
/** Working ranges on a date: an override wins (empty = day off), else the weekly pattern. */
export function rangesOn(availability, date, timezone) {
    const override = availability.overrides?.[date];
    if (override)
        return override;
    const weekday = localDate(wallToUtc(date, "12:00", timezone), timezone).weekday;
    return availability.weekly?.[weekday] ?? [];
}
/**
 * Free slots for one doctor on `date`. A slot must fit inside a working range, keep
 * `bufferMinutes` clear of every busy period, start at least `minNoticeHours` after `now`
 * and no later than `maxAdvanceDays` ahead.
 */
export function slotsOn(date, availability, rules, busy, timezone, now) {
    const step = rules.stepMinutes ?? (rules.durationMinutes > 45 ? 15 : rules.durationMinutes);
    const earliest = now.getTime() + rules.minNoticeHours * 3_600_000;
    const latest = now.getTime() + rules.maxAdvanceDays * 86_400_000;
    const out = [];
    for (const r of rangesOn(availability, date, timezone)) {
        const from = wallToUtc(date, r.from, timezone).getTime();
        const to = wallToUtc(date, r.to, timezone).getTime();
        for (let s = from; s + rules.durationMinutes * 60_000 <= to; s += step * 60_000) {
            const e = s + rules.durationMinutes * 60_000;
            if (s < earliest || s > latest)
                continue;
            const clash = busy.some((b) => s < b.endAt.getTime() + rules.bufferMinutes * 60_000 &&
                e + rules.bufferMinutes * 60_000 > b.startAt.getTime());
            if (!clash)
                out.push({ startAt: new Date(s), endAt: new Date(e) });
        }
    }
    return out;
}
/** Google Calendar "add event" link (11.1 step 8). */
export function googleCalendarUrl(input) {
    const fmt = (d) => d
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}/, "");
    const q = Object.entries({
        action: "TEMPLATE",
        text: input.title,
        dates: `${fmt(input.startAt)}/${fmt(input.endAt)}`,
        ...(input.details ? { details: input.details } : {}),
        ...(input.location ? { location: input.location } : {}),
    })
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");
    return `https://calendar.google.com/calendar/render?${q}`;
}
/** Minimal iCalendar file (as text) for Apple/Outlook calendars. */
export function icsText(input) {
    const fmt = (d) => d
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}/, "");
    const esc = (s) => s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/[,;]/g, "\\$&");
    return [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Wapadesk//Booking//EN",
        "BEGIN:VEVENT",
        `UID:${input.uid}`,
        `DTSTAMP:${fmt(new Date())}`,
        `DTSTART:${fmt(input.startAt)}`,
        `DTEND:${fmt(input.endAt)}`,
        `SUMMARY:${esc(input.title)}`,
        ...(input.description ? [`DESCRIPTION:${esc(input.description)}`] : []),
        ...(input.location ? [`LOCATION:${esc(input.location)}`] : []),
        ...(input.url ? [`URL:${input.url}`] : []),
        "END:VEVENT",
        "END:VCALENDAR",
    ].join("\r\n");
}
//# sourceMappingURL=booking.js.map